import { useId, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  booking,
  isLabNumberConflicts,
  type ArrivalEmailOutcome,
  type Booking,
  type BookInRequest,
  type BookInResponse,
  type HeldNegative,
  type LabNumberConflicts,
} from '../api/booking';
import { lab } from '../api/lab';
import { isApiError } from '../api/client';
import { filmStocks, type FilmStock } from '../api/filmStocks';
import { queryKeys } from '../api/queryKeys';
import { ModalDialog } from '../components/ModalDialog';
import { RollDetailsFields, type RollFieldsValue } from '../components/RollDetailsFields';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useAdminCall } from '../hooks/useAdminCall';
import { usePageTitle } from '../hooks/usePageTitle';
import type { NegativesPlan, RollFormat } from '../types/roll';
import {
  checkRollFields,
  describeNegatives,
  describeRoll,
  filmLabel,
  normaliseLabNumber,
  numberTheRest,
  PLAN_LABELS,
  shortDate,
  stageLabel,
  type RollFieldProblems,
} from '../utils/rollDetails';
import styles from './AdminBookInPage.module.css';

/** One roll the form will book in. */
interface Row extends RollFieldsValue {
  /** The roll id, chosen here so a retried booking finds the same rolls. */
  rollId: string;
  lineItemId: string | null;
  /** "Colour C-41 · 35mm · Develop and scan": the line it came from. */
  lineLabel: string | null;
}

function newRow(fields: Partial<Row> = {}): Row {
  return {
    rollId: crypto.randomUUID(),
    lineItemId: null,
    lineLabel: null,
    number: '',
    stock: null,
    kind: 'develop_and_scan',
    format: '35mm',
    process: '',
    ...fields,
  };
}

/** A row for every roll the Shopify lines say is coming and isn't booked yet. */
function expectedRows(data: Booking): Row[] {
  const rows = data.lines.flatMap((line) =>
    Array.from({ length: Math.max(0, line.quantity - line.booked) }, () =>
      newRow({
        lineItemId: line.line_item_id,
        lineLabel: describeRoll(line) || line.variant_title || line.title,
        kind: line.kind === 'unknown' ? 'develop_and_scan' : line.kind,
        format: line.format && line.format !== 'other' ? line.format : '',
        process: line.process ?? '',
      }),
    ),
  );
  // A walk-in, or an older order without its lines: start with one blank roll.
  return rows.length > 0 || data.lines.length > 0 ? rows : [newRow()];
}

const EMAIL_OUTCOME: Record<ArrivalEmailOutcome, string> = {
  sent: 'The customer has been emailed that their film has arrived.',
  already_sent: 'The arrival email had already gone.',
  no_email: 'No arrival email: the order has no email address.',
  not_enabled: 'No arrival email: order tracking is off for this customer.',
  failed: "The arrival email couldn't be sent. It will be tried again the next time you book rolls in.",
  not_requested: '',
};

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The booking form, started from what the order expects. Remounted after each booking. */
function BookInForm({
  data,
  stocks,
  onBooked,
}: {
  data: Booking;
  stocks: FilmStock[];
  onBooked: (response: BookInResponse, count: number) => void;
}) {
  const call = useAdminCall();
  const formId = useId();
  const [rows, setRows] = useState<Row[]>(() => expectedRows(data));
  const [plan, setPlan] = useState<NegativesPlan | ''>(data.suggested_plan ?? '');
  const [sendEmail, setSendEmail] = useState(data.arrival_email === 'will_send');
  const [problems, setProblems] = useState<Record<string, RollFieldProblems>>({});
  const [planProblem, setPlanProblem] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<LabNumberConflicts | null>(null);
  const numberRefs = useRef(new Map<string, HTMLInputElement>());
  const formRef = useRef<HTMLFormElement>(null);

  const bookIn = useMutation({
    mutationFn: (request: BookInRequest) => call(() => booking.bookIn(data.order.order_id, request)),
  });

  const fill = numberTheRest(rows);

  function update(rollId: string, patch: Partial<Row>) {
    setRows((all) => all.map((r) => (r.rollId === rollId ? { ...r, ...patch } : r)));
  }

  function focusNumber(rollId: string) {
    numberRefs.current.get(rollId)?.focus();
  }

  /** Check every row as the server would; focus the first problem. */
  function check(): boolean {
    const found: Record<string, RollFieldProblems> = {};
    const seen = new Map<string, string>();
    for (const row of rows) {
      const rowProblems = checkRollFields(row);
      const normalised = normaliseLabNumber(row.number);
      if (!rowProblems.number && normalised) {
        if (seen.has(normalised)) rowProblems.number = `${row.number.trim()} is on another roll here too`;
        seen.set(normalised, row.rollId);
      }
      if (Object.keys(rowProblems).length > 0) found[row.rollId] = rowProblems;
    }
    setProblems(found);
    setPlanProblem(!plan);
    if (Object.keys(found).length > 0 || !plan) {
      // Once the messages are showing, move to the first field that's wrong.
      setTimeout(() => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(), 0);
      return false;
    }
    return true;
  }

  async function submit(allowDuplicates: boolean) {
    setError(null);
    if (!plan) return;
    const request: BookInRequest = {
      rolls: rows.map((r) => ({
        roll_id: r.rollId,
        lab_roll_number: r.number.trim() || null,
        film_stock_id: r.stock!.stock_id,
        kind: r.kind,
        process: r.process || null,
        // check() has made sure every row has one.
        format: r.format as RollFormat,
        line_item_id: r.lineItemId,
      })),
      negatives_plan: plan,
      send_arrival_email: sendEmail,
      allow_duplicate_numbers: allowDuplicates,
    };
    try {
      const response = await bookIn.mutateAsync(request);
      onBooked(response, rows.length);
    } catch (e) {
      if (isApiError(e, 409) && isLabNumberConflicts(e.body)) {
        setConflicts(e.body);
        return;
      }
      setError((isApiError(e) && e.serverMessage) || 'Booking in failed. Try again.');
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (check()) void submit(false);
  }

  return (
    <form ref={formRef} className={styles.form} onSubmit={handleSubmit} noValidate aria-labelledby={`${formId}-heading`}>
      <h2 id={`${formId}-heading`} className={styles.sectionHeading}>
        Book in rolls
      </h2>
      {rows.length === 0 && <p className={styles.muted}>Every roll the order expects is booked in. Add a roll if more arrived.</p>}

      {rows.map((row, i) => (
        <fieldset key={row.rollId} className={styles.row}>
          <legend className={styles.legend}>
            Roll {i + 1} of {rows.length}
            {row.lineLabel && <span className={styles.lineLabel}> · {row.lineLabel}</span>}
          </legend>
          <RollDetailsFields
            value={row}
            onChange={(patch) => update(row.rollId, patch)}
            errors={problems[row.rollId]}
            stocks={stocks}
            numberRef={(el) => {
              if (el) numberRefs.current.set(row.rollId, el);
              else numberRefs.current.delete(row.rollId);
            }}
          />
          <button
            type="button"
            className={styles.linkBtn}
            onClick={() => setRows((all) => all.filter((r) => r.rollId !== row.rollId))}
          >
            Don't book in roll {i + 1}
          </button>
        </fieldset>
      ))}

      <div className={styles.rowActions}>
        <button type="button" className={styles.secondaryBtn} onClick={() => setRows((all) => [...all, newRow()])}>
          Add a roll
        </button>
        {fill && (
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() =>
              setRows((all) => all.map((r, i) => (fill.numbers[i] ? { ...r, number: fill.numbers[i]! } : r)))
            }
          >
            Number the rest from {fill.from}
          </button>
        )}
      </div>

      {rows.length > 0 && (
        <>
          <div className={styles.planField}>
            <label htmlFor={`${formId}-plan`} className={styles.label}>
              What happens to the negatives
            </label>
            <select
              id={`${formId}-plan`}
              className={styles.select}
              value={plan}
              onChange={(e) => setPlan(e.target.value as NegativesPlan | '')}
              aria-invalid={planProblem || undefined}
              aria-describedby={`${formId}-plan-hint`}
            >
              <option value="">Choose…</option>
              {Object.entries(PLAN_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <p id={`${formId}-plan-hint`} className={planProblem ? styles.fieldError : styles.hint}>
              {planProblem
                ? 'Choose what happens to the negatives'
                : data.order.shipping_method
                  ? `The customer chose “${data.order.shipping_method}”.${
                      data.order.negatives_option === 'hold' && data.suggested_plan === 'post_back'
                        ? ' This order ships film too, so the negatives can go back with it.'
                        : ''
                    }`
                  : data.order.walk_in
                    ? 'A walk-in: the customer usually collects.'
                    : 'Shopify gave no shipping choice for this order.'}
            </p>
          </div>

          <div className={styles.emailField}>
            {data.arrival_email === 'will_send' ? (
              <label className={styles.checkbox}>
                <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                Email {data.order.customer_name ?? 'the customer'} that their film has arrived
              </label>
            ) : (
              <p className={styles.muted}>
                {data.arrival_email === 'already_sent'
                  ? 'The arrival email has already gone for this order.'
                  : data.arrival_email === 'no_email'
                    ? 'No arrival email: the order has no email address.'
                    : 'No arrival email: order tracking is off for this customer.'}
              </p>
            )}
          </div>

          {error && (
            <p role="alert" className={styles.formError}>
              {error}
            </p>
          )}

          <div className={styles.submitRow}>
            <button type="submit" className={styles.primaryBtn} disabled={bookIn.isPending}>
              {bookIn.isPending ? 'Booking in…' : `Book in ${plural(rows.length, 'roll')}`}
            </button>
          </div>
        </>
      )}

      {conflicts && (
        <ModalDialog title="Lab number already used" onClose={() => setConflicts(null)}>
          <p>Labels come round again, so this may be right. Check the film against its label:</p>
          <ul className={styles.conflicts}>
            {conflicts.conflicts.map((c) => (
              <li key={`${c.number}-${c.order_id}`}>
                <strong>{c.number}</strong> was used on{' '}
                <Link to={`/admin/orders/${encodeURIComponent(c.order_id)}/upload`} target="_blank" rel="noopener">
                  {c.order_number}
                </Link>{' '}
                {c.days_ago === 0 ? 'today' : c.days_ago === 1 ? 'yesterday' : `${c.days_ago} days ago`}
              </li>
            ))}
          </ul>
          <div className={styles.dialogActions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => {
                const number = conflicts.conflicts[0]?.number;
                setConflicts(null);
                const row = rows.find((r) => normaliseLabNumber(r.number) === normaliseLabNumber(number ?? ''));
                if (row) setTimeout(() => focusNumber(row.rollId), 0);
              }}
            >
              Change the number
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => {
                setConflicts(null);
                void submit(true);
              }}
            >
              Book in anyway
            </button>
          </div>
        </ModalDialog>
      )}
    </form>
  );
}

/** Negatives held from the customer's other orders, which can go with this one. */
function HeldNegativesPanel({ data }: { data: Booking }) {
  const call = useAdminCall();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: (held: HeldNegative) =>
      call(() =>
        lab.negatives(held.order_id, held.roll_id, {
          action: 'returned_with_order',
          returned_with_order_id: data.order.order_id,
        }),
      ),
    onSuccess: (_roll, held) => {
      setMessage(`${held.roll_name} will go with ${data.order.order_number}.`);
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.booking(data.order.order_id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.labQueue() });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.rolls(held.order_id) });
    },
    onError: (e) => setMessage((isApiError(e) && e.serverMessage) || 'That didn’t save. Try again.'),
  });
  const held = data.held_negatives ?? [];
  if (held.length === 0 && !message) return null;
  return (
    <section className={styles.section} aria-labelledby="held-heading">
      <h2 id="held-heading" className={styles.sectionHeading}>
        Negatives we're holding for {data.order.customer_name ?? 'this customer'}
      </h2>
      <p className={styles.muted}>They were waiting for a companion order. If this is it, send them with it.</p>
      <ul className={styles.booked}>
        {held.map((h) => (
          <li key={h.roll_id} className={styles.heldRow}>
            <span>
              <strong>{h.roll_name}</strong>
              <span className={styles.muted}>
                {' '}
                · from{' '}
                <Link to={`/admin/orders/${encodeURIComponent(h.order_id)}/upload`}>{h.order_number ?? h.order_id}</Link>
                {h.held_until && (h.overdue ? ` · hold ended ${shortDate(h.held_until)}` : ` · held until ${shortDate(h.held_until)}`)}
              </span>
            </span>
            <button type="button" className={styles.secondaryBtn} disabled={send.isPending} onClick={() => send.mutate(h)}>
              Send with {data.order.order_number}
            </button>
          </li>
        ))}
      </ul>
      {message && (
        <p role="status" className={styles.muted}>
          {message}
        </p>
      )}
    </section>
  );
}

/**
 * Booking an order's rolls in when the film arrives: a row per roll the
 * Shopify order says is coming, with its lab number, film stock, service,
 * format and process, what happens to the negatives, and the customer's one
 * "your film has arrived" email.
 */
export function AdminBookInPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const { logout } = useAdminAuth();
  const call = useAdminCall();
  const queryClient = useQueryClient();
  const [announcement, setAnnouncement] = useState('');
  const [round, setRound] = useState(0);

  // The form starts from this data, so it mustn't change under the
  // technician's typing: no refetch on window focus, only after a booking.
  const bookingQuery = useQuery({
    queryKey: queryKeys.admin.booking(orderId),
    queryFn: () => call(() => booking.get(orderId!)),
    enabled: !!orderId,
    refetchOnWindowFocus: false,
  });
  const stocksQuery = useQuery({
    queryKey: queryKeys.admin.filmStocks(),
    queryFn: () => call(filmStocks.list),
  });
  const data = bookingQuery.data;
  usePageTitle(data ? `Book in ${data.order.order_number}` : 'Book in');


  return (
    <main className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.heading}>Book in {data?.order.order_number ?? ''}</h1>
        <div className={styles.headerActions}>
          <Link to="/admin/orders" className={styles.secondaryBtn}>
            Orders
          </Link>
          {orderId && (
            <Link to={`/admin/orders/${encodeURIComponent(orderId)}/upload`} className={styles.secondaryBtn}>
              Upload scans
            </Link>
          )}
          <button type="button" onClick={logout} className={styles.secondaryBtn}>
            Logout
          </button>
        </div>
      </div>

      <div role="status" aria-live="polite" className={styles.announcement}>
        {announcement}
      </div>

      {bookingQuery.isLoading && <p className={styles.muted}>Loading the order…</p>}
      {bookingQuery.error && (
        <p role="alert" className={styles.formError}>
          {isApiError(bookingQuery.error, 404) ? 'That order was not found.' : 'Error loading the order.'}
        </p>
      )}

      {data && (
        <>
          <p className={styles.orderFacts}>
            {data.order.customer_name ?? data.order.customer_id} · ordered {shortDate(data.order.order_date)}
            {data.order.shipping_method && ` · ${data.order.shipping_method}`}
          </p>
          {data.order.cancelled_at && (
            <p role="alert" className={styles.warning}>
              Shopify says this order was cancelled on {shortDate(data.order.cancelled_at)}.
            </p>
          )}
          {data.order.scan_ready && (
            <p className={styles.warning}>This order is already marked ready.</p>
          )}

          <section className={styles.section} aria-labelledby="expected-heading">
            <h2 id="expected-heading" className={styles.sectionHeading}>
              What the customer sent
            </h2>
            {data.lines.length === 0 ? (
              <p className={styles.muted}>
                {data.order.walk_in
                  ? 'A walk-in: add each roll below.'
                  : 'Shopify gave no lab lines for this order. Add each roll below.'}
              </p>
            ) : (
              <ul className={styles.lines}>
                {data.lines.map((line) => (
                  <li key={line.line_item_id} className={styles.line}>
                    <span className={styles.lineTitle}>
                      {line.quantity} × {describeRoll(line) || line.variant_title || line.title}
                    </span>
                    <span className={line.booked >= line.quantity ? styles.done : styles.muted}>
                      {line.booked} of {line.quantity} booked in
                    </span>
                    {line.special_instructions && (
                      <p className={styles.instructions}>
                        <strong>Special instructions:</strong> {line.special_instructions}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <HeldNegativesPanel data={data} />

          {data.rolls.length > 0 && (
            <section className={styles.section} aria-labelledby="booked-heading">
              <h2 id="booked-heading" className={styles.sectionHeading}>
                Booked in ({data.rolls.length})
              </h2>
              <ul className={styles.booked}>
                {data.rolls.map((roll) => {
                  const negatives = roll.tracking ? describeNegatives(roll.tracking.negatives) : null;
                  return (
                    <li key={roll.roll_id}>
                      <strong>{roll.roll_name}</strong>
                      <span className={styles.muted}>
                        {' '}
                        ·{' '}
                        {[
                          // The default name already says the film.
                          roll.roll_name.includes(filmLabel(roll) ?? '\u0000') ? null : filmLabel(roll),
                          describeRoll(roll),
                          stageLabel(roll),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        {negatives && ` · negatives ${negatives.text}`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {stocksQuery.data ? (
            <BookInForm
              key={round}
              data={data}
              stocks={stocksQuery.data}
              onBooked={(response, count) => {
                const existed = response.rolls.filter((r) => r.outcome === 'exists').length;
                setAnnouncement(
                  [
                    `Booked in ${plural(count - existed, 'roll')}.`,
                    existed > 0 ? `${plural(existed, 'roll was', 'rolls were')} already booked in.` : '',
                    EMAIL_OUTCOME[response.arrival_email],
                  ]
                    .filter(Boolean)
                    .join(' '),
                );
                queryClient.invalidateQueries({ queryKey: queryKeys.admin.rolls(orderId) });
                queryClient.invalidateQueries({ queryKey: queryKeys.admin.orders() });
                // Start a fresh form from what's left to book, once it's known.
                void queryClient
                  .invalidateQueries({ queryKey: queryKeys.admin.booking(orderId) })
                  .finally(() => setRound((r) => r + 1));
              }}
            />
          ) : stocksQuery.error ? (
            <p role="alert" className={styles.formError}>
              Couldn't load the film stocks.
            </p>
          ) : (
            <p className={styles.muted}>Loading film stocks…</p>
          )}
        </>
      )}
    </main>
  );
}
